import { useState } from 'react';
import styled from 'styled-components';

import Alert from 'components/shared/Alert';
import Button from 'components/shared/Button';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import { getDefaultStyles } from 'components/shared/form/Select/customStyles';
import StandardModal, { Actions } from 'components/shared/StandardModal';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import { SyncPatientToEhrAction } from 'models/SyncPatientToEhrAction';
import Workflow, { WorkflowGroupOptions } from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { sortedGroupsByName } from '../FanoutPanel';

import { platformLabel } from './SyncPatientBody';

interface Props {
  workflow: Workflow;
  onUpdateValues: (values: WorkflowValuesInput) => Promise<unknown>;
  // The trigger lives in ReferralOutcomePanel's winner-row ⋯ menu now (design review) rather than
  // a link owned by this component — the parent mounts this component only while open, and this is
  // how it's told to unmount itself (Cancel, or a successful save).
  onClose: () => void;
}

// Matches the modal body rhythm the rest of this feature's confirm dialogs use (e.g.
// eligibility/RerunConfirmModal's Content) — StandardModal's own StyledContent padding has no gap
// between siblings, so without this wrapper the field, warnings, and Actions row all sat flush
// against each other instead of evenly spaced (OLIO-2846 QA: "looks different than normal").
const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Warning = styled(BodySmall)`
  color: ${colors.orangeHover};
`;

const SYNC_KIND = 'SyncPatientToEhr';

/**
 * Changes which facility a Selected referral was won for (OLIO-2846).
 *
 * It lives in the sync folder because the sync is the only reason it exists: the outcome result was
 * read-only, and a coordinator who moved a patient to a different building had no way to record it —
 * which meant no way to push the patient to the right EHR facility either.
 *
 * A plain VALUE WRITE, not an amend. `amend` cannot work here: run_action returns nil on a decided
 * run, so amending a terminal referral's action is a silent no-op. A MANUAL write of
 * selected_facility_id (rank 3) outranks AcceptReferral's ACTION value (rank 1), and write_values is
 * deliberately not terminal-gated, so this is the supported path. The API re-checks that the new
 * building is one the referral actually targets — same rule, same message as AcceptReferral's
 * completion-time guard.
 */
export default function ChangeSelectedFacility({ workflow, onUpdateValues, onClose }: Props) {
  const { canEdit } = useWorkflowPermissions();
  const [saving, setSaving] = useState(false);
  const currentId = workflow.valueFor('selected_facility_id')?.value ?? '';
  const [facilityId, setFacilityId] = useState(currentId);

  // Hidden for viewers (found in QA on OLIO-2973): the value write is WorkflowPolicy#update?, so
  // blanket workflow edit is the grant — not canEditAction, which a kind-grant specialist can pass
  // while this write still 403s. UX only; the API enforces the same rule regardless.
  if (!canEdit) return null;

  // Only buildings this referral targets — mirroring the server guard, so the picker cannot offer
  // something the API will refuse.
  const facilities = sortedGroupsByName(workflow.groups);

  // A push that already LANDED somewhere. Its facility is what makes the warning below necessary and
  // specific: there is no un-sync API at either vendor, so the first record survives the change.
  //
  // ALL the siblings, not `find` — the sync is per-building now, so a run carries one instance per
  // targeted building and `find` returned whichever the API listed first. That is usually a `skipped`
  // loser, which reports nothing landed, so the "already synced to X, must be discharged there"
  // warning silently disappeared — at the exact moment a coordinator moves a patient, which is the
  // only moment it exists for.
  //
  // `isSyncedSuccessfully`, not `isCompleted` — duplicate/locked/matched/no_match also arrive completed
  // and name the facility they were ATTEMPTED against. Keying on completion told a coordinator a
  // record exists in a building where a duplicate had in fact created nothing. The getter is shared
  // with SyncPatientBody so the two cannot drift back apart.
  const syncs = workflow.actions.filter((action) => action.kind === SYNC_KIND) as SyncPatientToEhrAction[];
  const landed = syncs.find((action) => action.isSyncedSuccessfully);
  const syncedGroupId = landed?.syncedFacilityGroupId ?? null;
  const syncedName = facilities.find((group) => group.id === syncedGroupId)?.name;

  // A push that is IN FLIGHT right now — a distinct warning from the completed one, and the one a
  // coordinator is least likely to reason out for themselves. Changing the winner cannot recall a
  // vendor call already on its way: it will land at the OLD facility and create a record there, and
  // the result will come back and settle against this referral afterwards. Raised in review on
  // olio-api#3293 as a product note, because the API cannot fix it — nobody can un-send a SOAP call.
  // ANY sibling in flight, for the same reason: the push that cannot be recalled may belong to a
  // building other than whichever instance happened to come back first.
  //
  // A send of documents to an existing record is in flight too, but it adds no resident, so it gets its
  // own line rather than the "will create a record" one.
  const sendingNow = syncs.some((action) => action.isRunning && !action.isDocumentsMode);
  const sendingDocumentsNow = syncs.some((action) => action.isRunning && action.isDocumentsMode);

  const save = async () => {
    setSaving(true);
    try {
      await onUpdateValues({ selected_facility_id: facilityId });
      onClose();
    } catch {
      // Stay open and swallow, the same shape as the sync modal's send. Without the catch a rejected
      // write (a 422 for a building the referral doesn't target) escaped as an unhandled rejection —
      // the toast still showed and the modal still stayed open, so the user saw the right thing, but
      // the noise would have buried a real failure in the console.
    } finally {
      setSaving(false);
    }
  };

  return (
    <StandardModal title='Change selected facility' onCancel={onClose} maxWidth='560px'>
      <ModalBody>
        <Field>
          <FieldLabel name='change-facility'>Which facility did the patient select?</FieldLabel>
          <Select<WorkflowGroupOptions>
            inputId='change-facility'
            options={facilities}
            value={facilities.find((group) => group.id === facilityId) ?? null}
            onChange={(group) => setFacilityId(group?.id ?? '')}
            getOptionValue={(group) => group.id}
            getOptionLabel={(group) => group.name}
            placeholder='Select a facility'
            dataTestId='change-facility-select'
            menuPortalTarget={document.body}
            // The menu is portalled to <body> to escape the modal's clipping, and react-select
            // defaults a portalled menu to z-index 1 — below StandardModal's 901. Without this it
            // opened BEHIND the dialog: every facility was in the list, nothing was visible, and
            // the control read as disabled because clicking it appeared to do nothing.
            //
            // Styles are spread rather than replaced because a `styles` prop OVERRIDES the
            // component's own `styles={getDefaultStyles()}` (it is applied before the passthrough
            // spread), so passing only menuPortal here would drop the house look entirely.
            //
            // Local to this call site on purpose: Select's sibling MultiSelectChips already does
            // this internally, so the real fix is to make Select consistent with it — but that
            // touches every portalled Select in the app and belongs in its own PR, not this one.
            styles={{ ...getDefaultStyles(), menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Field>

        {/* Always shown (OLIO-3148): changing the winner flips the previously-selected building to
                Not Selected, and the Figma calls for spelling that out so the coordinator isn't surprised
                by the old facility's status changing. Info styling (Alert), distinct from the orange
                un-sync warnings below. */}
        <Alert data-testid='change-facility-update-note'>
          <Alert.Text>
            <strong>Note</strong>: The previously ‘Selected’ facility will be updated to ‘Not Selected’
          </Alert.Text>
        </Alert>

        {/* Only shown when a push actually landed — an unsynced referral has nothing to warn about,
                and a warning that always appears is one nobody reads. Neither MatrixCare nor PCC has an
                un-sync API, so somebody has to discharge or reject the old record by hand. */}
        {sendingNow && (
          <Warning data-testid='change-facility-in-flight-warning'>
            This patient is being sent to {platformLabel(workflow.ehrSync.platform)} right now. Changing the winner will
            not stop it — that send will still create a record at the facility it was started for, which must then be
            discharged or rejected there.
          </Warning>
        )}

        {sendingDocumentsNow && (
          <Warning data-testid='change-facility-in-flight-warning'>
            Documents are being sent to this patient’s existing record in {platformLabel(workflow.ehrSync.platform)}{' '}
            right now. Changing the winner will not stop them — they will still be added to that record.
          </Warning>
        )}

        {/* Only documents went to a chart the patient already had — Olio created no record there, so
            there is nothing of ours to discharge or reject. */}
        {syncedName && landed?.isDocumentsAdded && (
          <Warning data-testid='change-facility-synced-warning'>
            Documents from this referral were added to this patient’s existing record in {syncedName} in{' '}
            {platformLabel(workflow.ehrSync.platform)}. Changing the winner lets you push to the new facility.
          </Warning>
        )}

        {syncedName && !landed?.isDocumentsAdded && (
          <Warning data-testid='change-facility-synced-warning'>
            This patient was already pushed to {syncedName} in {platformLabel(workflow.ehrSync.platform)}. Changing the
            winner lets you push to the new facility; the record in {syncedName} is not removed and must be discharged
            or rejected there.
          </Warning>
        )}

        <Actions>
          <Button variant='ghost' onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={saving || !facilityId || facilityId === currentId}
            onClick={save}
            data-testid='change-facility-confirm'>
            {saving ? 'Saving…' : 'Change facility'}
          </Button>
        </Actions>
      </ModalBody>
    </StandardModal>
  );
}
